/**
 * Device capability detection.
 *
 * Deliberately keyed off the *pointer* being coarse rather than "is this a phone" — a
 * touchscreen laptop gets touch controls only if it actually has no fine pointer, and a
 * Windows tablet in desktop mode keeps the mouse UI. `maxTouchPoints` alone is not enough:
 * plenty of desktop browsers report it as non-zero.
 */

let forcedTouch: boolean | null = null;

/** Test hook: force touch controls on/off regardless of the real device. */
export function setForceTouch(v: boolean | null): void {
  forcedTouch = v;
}

export function isTouchDevice(): boolean {
  if (forcedTouch !== null) return forcedTouch;

  // Two separate questions, and BOTH must be yes:
  //
  //   1. Can this thing do touch at all?
  //      `maxTouchPoints` alone is not reliable — WebKit reports 0 on a device that handles
  //      touch perfectly well — so accept `ontouchstart` as well.
  //
  //   2. Is touch the PRIMARY way this user points at things?
  //      Desktop Chrome exposes `ontouchstart` on plenty of machines that have no touchscreen,
  //      and a touchscreen laptop still has a mouse. Answering only question 1 turns the
  //      on-screen joystick on — and hides the cursor — on an ordinary desktop.
  //
  // A coarse pointer / no hover means the pointing device really is a finger.
  const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
  const primaryIsTouch = (window.matchMedia?.('(pointer: coarse)').matches ?? false)
                      || (window.matchMedia?.('(hover: none)').matches ?? false);
  return hasTouch && primaryIsTouch;
}

/** True when the viewport is taller than it is wide. */
export function isPortrait(): boolean {
  return window.innerHeight > window.innerWidth;
}

/**
 * A 16:9 action game in portrait is a thin useless strip, so we ask phones to rotate.
 * Tablets are big enough that the letterboxed strip is still playable, so they are exempt.
 */
export function shouldAskToRotate(): boolean {
  if (!isTouchDevice()) return false;
  if (!isPortrait()) return false;
  const shortSide = Math.min(window.innerWidth, window.innerHeight);
  return shortSide < 820; // phones and small tablets; iPad portrait (768) included
}

/** Request fullscreen + landscape lock. Both are best-effort: iOS Safari supports neither. */
export async function goFullscreenLandscape(el: HTMLElement): Promise<void> {
  try {
    if (!document.fullscreenElement && el.requestFullscreen) {
      await el.requestFullscreen({ navigationUI: 'hide' } as FullscreenOptions);
    }
  } catch {
    // Refused (iOS, or no user gesture) — the game still plays, just with browser chrome.
  }
  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>;
    };
    await orientation?.lock?.('landscape');
  } catch {
    // Unsupported (iOS) or not fullscreen — the rotate prompt covers this case.
  }
}
